import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormGetValues,
} from 'react-hook-form';
import { IconMinus, IconPlus } from '@tabler/icons-react';

import { Button, Input, Label, Surface, TextField, toast } from '@heroui/react';
import { trpc } from '@/lib/trpc';

export type FeatureChildFormValues = {
  code: string;
  title: string;
  description: string;
  sort: number;
};

export type FeatureGroupFormValues = {
  code: string;
  title: string;
  sort: number;
  children: FeatureChildFormValues[];
};

export type FeatureConfigFormValues = {
  featureGroups: FeatureGroupFormValues[];
};

export type FeatureConfigItem = {
  code: string;
  title: string;
  description: string;
  sort: number;
};

/** 功能对照模板来源 SKU 编码；导入时取其 metadata.featureConfig */
const FEATURE_CONFIG_IMPORT_SOURCE_CODE = 'PERSONAL_PLAN_FREE';

export function createEmptyChild(parentCode = '', sort = 1): FeatureChildFormValues {
  const code = parentCode ? `${parentCode}.${String(sort).padStart(2, '0')}` : '';
  return { code, title: '', description: '', sort };
}

export function createEmptyGroup(sort = 1, code = ''): FeatureGroupFormValues {
  const majorCode = code || String.fromCharCode(64 + Math.min(Math.max(sort, 1), 26));
  return {
    code: majorCode,
    title: '',
    sort,
    children: [createEmptyChild(majorCode, 1)],
  };
}

/** 扁平 feature_config → 表单大项/小项分组（仅一、二级） */
export function parseFeatureConfigToGroups(
  items: FeatureConfigItem[] | null | undefined
): FeatureGroupFormValues[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [createEmptyGroup(1, 'A')];
  }

  const majors = items
    .filter((item) => item.code.split('.').length === 1)
    .slice()
    .sort((a, b) => a.sort - b.sort || a.code.localeCompare(b.code));

  const minors = items.filter((item) => item.code.split('.').length === 2);

  if (majors.length === 0) {
    return [createEmptyGroup(1, 'A')];
  }

  return majors.map((major) => {
    const children = minors
      .filter((child) => child.code.startsWith(`${major.code}.`))
      .slice()
      .sort((a, b) => a.sort - b.sort || a.code.localeCompare(b.code))
      .map((child) => ({
        code: child.code,
        title: child.title ?? '',
        description: child.description ?? '',
        sort: child.sort,
      }));

    return {
      code: major.code,
      title: major.title ?? '',
      sort: major.sort,
      children: children.length > 0 ? children : [],
    };
  });
}

/** 表单分组 → 扁平 feature_config；仅保留编码+标题完整的项 */
export function flattenFeatureGroups(
  groups: FeatureGroupFormValues[] | undefined
): FeatureConfigItem[] | null {
  if (!groups?.length) return null;

  const items: FeatureConfigItem[] = [];

  for (const group of groups) {
    const code = group.code.trim().toUpperCase();
    const title = group.title.trim();
    const sort = Number(group.sort) || 1;

    const children = (group.children ?? [])
      .map((child) => ({
        code: child.code.trim().toUpperCase(),
        title: child.title.trim(),
        description: child.description.trim(),
        sort: Number(child.sort) || 1,
      }))
      .filter((child) => Boolean(child.code && child.title));

    if (!code || !title) continue;

    items.push({
      code,
      title,
      description: '',
      sort,
    });

    for (const child of children) {
      items.push(child);
    }
  }

  return items.length > 0 ? items : null;
}

function nextMajorCode(existing: string[]): string {
  const used = new Set(existing.map((c) => c.trim().toUpperCase()).filter(Boolean));
  for (let i = 0; i < 26; i += 1) {
    const code = String.fromCharCode(65 + i);
    if (!used.has(code)) return code;
  }
  return 'Z';
}

interface FeatureGroupSurfaceProps {
  control: Control<FeatureConfigFormValues>;
  groupIndex: number;
  onRemove: () => void;
  errors?: FieldErrors<FeatureConfigFormValues>;
}

function FeatureGroupSurface({
  control,
  groupIndex,
  onRemove,
  errors,
}: FeatureGroupSurfaceProps) {
  const parentCode = useWatch({
    control,
    name: `featureGroups.${groupIndex}.code`,
  });

  const {
    fields: childFields,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({
    control,
    name: `featureGroups.${groupIndex}.children`,
  });

  const groupError = errors?.featureGroups?.[groupIndex];

  return (
    <Surface variant="secondary" className="flex flex-col gap-4 rounded-2xl p-4">
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">大项</Label>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)_4.5rem_2rem] items-end gap-2">
          <Controller
            control={control}
            name={`featureGroups.${groupIndex}.code`}
            render={({ field }) => (
              <TextField className="w-full" isInvalid={!!groupError?.code}>
                <Label className="text-xs text-muted-foreground">编码</Label>
                <Input
                  aria-label="大项编码"
                  maxLength={1}
                  placeholder="A"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))
                  }
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </TextField>
            )}
          />
          <Controller
            control={control}
            name={`featureGroups.${groupIndex}.title`}
            render={({ field }) => (
              <TextField className="w-full" isInvalid={!!groupError?.title}>
                <Label className="text-xs text-muted-foreground">标题</Label>
                <Input
                  aria-label="大项标题"
                  maxLength={100}
                  placeholder="功能模块"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </TextField>
            )}
          />
          <Controller
            control={control}
            name={`featureGroups.${groupIndex}.sort`}
            render={({ field }) => (
              <TextField className="w-full" isInvalid={!!groupError?.sort}>
                <Label className="text-xs text-muted-foreground">排序</Label>
                <Input
                  aria-label="大项排序"
                  type="number"
                  min={1}
                  placeholder="1"
                  value={
                    field.value === undefined || field.value === null ? '' : String(field.value)
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    field.onChange(raw === '' ? 1 : Number(raw));
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </TextField>
            )}
          />
          <Button
            isIconOnly
            aria-label="删除大项"
            className="mb-0.5"
            size="sm"
            variant="ghost"
            onPress={onRemove}
          >
            <IconMinus className="size-4" stroke={1.5} />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">小项</Label>
        {childFields.length > 0 ? (
          <div className="grid grid-cols-[5.5rem_minmax(0,0.9fr)_minmax(0,1.2fr)_4.5rem_2rem] gap-2 px-0.5">
            <span className="text-xs text-muted-foreground">编码</span>
            <span className="text-xs text-muted-foreground">标题</span>
            <span className="text-xs text-muted-foreground">说明</span>
            <span className="text-xs text-muted-foreground">排序</span>
            <span />
          </div>
        ) : null}
        {childFields.map((child, childIndex) => {
          const childError = groupError?.children?.[childIndex];
          return (
            <div
              key={child.id}
              className="grid grid-cols-[5.5rem_minmax(0,0.9fr)_minmax(0,1.2fr)_4.5rem_2rem] items-center gap-2"
            >
              <Controller
                control={control}
                name={`featureGroups.${groupIndex}.children.${childIndex}.code`}
                render={({ field }) => (
                  <TextField className="w-full" isInvalid={!!childError?.code} aria-label="小项编码">
                    <Input
                      maxLength={5}
                      placeholder="A.01"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9.]/g, ''))
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name={`featureGroups.${groupIndex}.children.${childIndex}.title`}
                render={({ field }) => (
                  <TextField className="w-full" isInvalid={!!childError?.title} aria-label="小项标题">
                    <Input
                      maxLength={100}
                      placeholder="功能名称"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name={`featureGroups.${groupIndex}.children.${childIndex}.description`}
                render={({ field }) => (
                  <TextField
                    className="w-full"
                    isInvalid={!!childError?.description}
                    aria-label="小项说明"
                  >
                    <Input
                      maxLength={200}
                      placeholder="支持 / 1人 / 限时免费"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name={`featureGroups.${groupIndex}.children.${childIndex}.sort`}
                render={({ field }) => (
                  <TextField className="w-full" isInvalid={!!childError?.sort} aria-label="小项排序">
                    <Input
                      type="number"
                      min={1}
                      placeholder="1"
                      value={
                        field.value === undefined || field.value === null
                          ? ''
                          : String(field.value)
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        field.onChange(raw === '' ? 1 : Number(raw));
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </TextField>
                )}
              />
              <Button
                isIconOnly
                aria-label="删除小项"
                size="sm"
                variant="ghost"
                onPress={() => removeChild(childIndex)}
              >
                <IconMinus className="size-4" stroke={1.5} />
              </Button>
            </div>
          );
        })}

        <Button
          className="self-start"
          size="sm"
          variant="ghost"
          onPress={() => {
            const nextSort = childFields.length + 1;
            const code = (parentCode ?? '').trim().toUpperCase() || 'A';
            appendChild(createEmptyChild(code, nextSort));
          }}
        >
          <IconPlus className="size-4" stroke={1.5} />
          再添加一条小项
        </Button>
      </div>
    </Surface>
  );
}

interface FeatureConfigEditorProps {
  control: Control<FeatureConfigFormValues>;
  getValues: UseFormGetValues<FeatureConfigFormValues>;
  errors?: FieldErrors<FeatureConfigFormValues>;
}

export function FeatureConfigEditor({ control, getValues, errors }: FeatureConfigEditorProps) {
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'featureGroups',
  });

  const utils = trpc.useUtils();

  const handleImport = async () => {
    try {
      const sku = await utils.bizProduct.sku.getByCode.fetch({
        code: FEATURE_CONFIG_IMPORT_SOURCE_CODE,
      });
      const featureConfig = (sku.metadata as { featureConfig?: unknown } | null)?.featureConfig;
      if (!Array.isArray(featureConfig) || featureConfig.length === 0) {
        toast.danger('模板 SKU 的 featureConfig 为空，无法导入！');
        return;
      }
      // 新建/编辑均完全替换当前功能配置
      replace(parseFeatureConfigToGroups(featureConfig as FeatureConfigItem[]));
      toast.success('功能配置已导入');
    } catch (error) {
      toast.danger('导入失败', {
        description: error instanceof Error ? error.message : '请稍后重试',
      });
    }
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">功能配置</Label>
        <Button size="sm" variant="tertiary" onPress={() => void handleImport()}>
          导入
        </Button>
      </div>

      {fields.map((group, groupIndex) => (
        <FeatureGroupSurface
          key={group.id}
          control={control}
          groupIndex={groupIndex}
          errors={errors}
          onRemove={() => remove(groupIndex)}
        />
      ))}

      <Button
        className="self-start"
        size="sm"
        variant="ghost"
        onPress={() => {
          const existing = getValues('featureGroups') ?? [];
          const nextSort = existing.length + 1;
          const code = nextMajorCode(existing.map((g) => g.code));
          append(createEmptyGroup(nextSort, code));
        }}
      >
        <IconPlus className="size-4" stroke={1.5} />
        再添加一条大项
      </Button>
    </div>
  );
}
